import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { Canceler } from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  CondoRequestTypeEnum,
  IBounds,
  ICondo,
  ICondoCounters,
  ICondoLocation,
  ICondoRecentSearches,
  ICondoSearchResponse,
  ISessionKey,
  SearchTypeEnum,
} from 'src/common-types/index';
import { DEFAULT_PAGE_NUMBER, DEFAULT_MAP_LIMIT, SortTypes, Urls } from 'src/constants/index';
import {
  axiosInstance,
  getCondoRequestType,
  getCondoRequestUrl,
  getCondoSearchBody,
  getHeaders,
  getTimeout,
  shouldDisplayAnytimeWarning,
} from 'src/utils/index';
import { trustYouReviewActions } from './trust-you-review';
import { condoDetailsActions } from './condo-details';
import { condoFiltersActions } from './condo-filters';
import { AppThunk, RootState } from '../index';

const zero = 0;

export interface ICondosState {
  isSearch: boolean;
  condos: ICondo[];
  prevCondos: ICondo[];
  mapCondos: ICondo[];
  loading: boolean;
  loadingMore: boolean;
  error: string;
  pageNumber: number;
  counters: ICondoCounters;
  sessionKey: ISessionKey;
  isSessionExpired: boolean;
  bounds: IBounds;
  cachedSelectedLocation: ICondoLocation;
  isFinishedWithError: boolean;
  lastRequestType: CondoRequestTypeEnum;
  cachedStartDate: string;
  cachedEndDate: string;
  selectedCondo: ICondo;
  isFocusDatePicker: boolean;
  recentSearches: ICondoRecentSearches[];
  errorRecentSearches: string;
  loadingRecentSearches: boolean;
  isAnytimeSearchWarningVisible: boolean;
}

const initialState: ICondosState = {
  isSearch: false,
  condos: null,
  prevCondos: null,
  mapCondos: null,
  loading: false,
  loadingMore: false,
  error: '',
  pageNumber: DEFAULT_PAGE_NUMBER,
  counters: null,
  sessionKey: null,
  isSessionExpired: false,
  bounds: null,
  cachedSelectedLocation: null,
  isFinishedWithError: false,
  lastRequestType: CondoRequestTypeEnum.Anytime,
  cachedStartDate: null,
  cachedEndDate: null,
  selectedCondo: null,
  isFocusDatePicker: false,
  recentSearches: [],
  errorRecentSearches: '',
  loadingRecentSearches: false,
  isAnytimeSearchWarningVisible: false,
};

const condosSlice = createSlice({
  name: 'condos',
  initialState,
  reducers: {
    setIsSearch: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.isSearch = payload;
    },
    setLastRequestType: (state: ICondosState, { payload }: PayloadAction<CondoRequestTypeEnum>) => {
      state.lastRequestType = payload;
    },
    setIsFinishedWithError: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.isFinishedWithError = payload;
    },
    setLoading: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingMore: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.loadingMore = payload;
    },
    setError: (state: ICondosState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setPageNumber: (state: ICondosState, { payload }: PayloadAction<number>) => {
      state.pageNumber = payload;
    },
    setCounters: (state: ICondosState, { payload }: PayloadAction<ICondoCounters>) => {
      state.counters = payload;
    },
    setSessionKey: (state: ICondosState, { payload }: PayloadAction<ISessionKey>) => {
      state.sessionKey = payload;
    },
    setIsSessionExpired: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.isSessionExpired = payload;
    },
    setCondos: (state: ICondosState, { payload }: PayloadAction<ICondo[]>) => {
      state.condos = payload;
    },
    setPrevCondos: (state: ICondosState, { payload }: PayloadAction<ICondo[]>) => {
      state.prevCondos = payload;
    },
    setBounds: (state: ICondosState, { payload }: PayloadAction<IBounds>) => {
      state.bounds = payload;
    },
    setCachedSelectedLocation: (
      state: ICondosState,
      { payload }: PayloadAction<ICondoLocation>,
    ) => {
      state.cachedSelectedLocation = payload;
    },
    addCondos: (state: ICondosState, { payload }: PayloadAction<ICondo[]>) => {
      state.condos = [...(state.condos || []), ...payload];
    },
    setSelectedCondo: (state: ICondosState, { payload }: PayloadAction<ICondo>) => {
      state.selectedCondo = payload;
    },
    setMapCondos: (state: ICondosState, { payload }: PayloadAction<ICondo[]>) => {
      state.mapCondos = payload;
    },
    addMapCondo: (state: ICondosState, { payload }: PayloadAction<ICondo>) => {
      state.mapCondos.push(payload);
    },
    setCachedDates: (
      state: ICondosState,
      { payload }: PayloadAction<{ startDate: string; endDate: string }>,
    ) => {
      state.cachedStartDate = payload.startDate;
      state.cachedEndDate = payload.endDate;
    },
    setIsFocusDatePicker: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.isFocusDatePicker = payload;
    },
    setRecentSearches: (
      state: ICondosState,
      { payload }: PayloadAction<ICondosState['recentSearches']>,
    ) => {
      state.recentSearches = payload;
    },
    setLoadingRecentSearches: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.loadingRecentSearches = payload;
    },
    setErrorRecentSearches: (state: ICondosState, { payload }: PayloadAction<string>) => {
      state.errorRecentSearches = payload;
    },
    setDefaultValues: () => {
      return initialState;
    },
    setIsAnytimeSearchWarningVisible: (state: ICondosState, { payload }: PayloadAction<boolean>) => {
      state.isAnytimeSearchWarningVisible = payload;
    },
  },
});

export const condosActions = condosSlice.actions;

export const condosReducer = condosSlice.reducer;

let cancelRequest: Canceler;
let expirationTimer: number;

const setSessionExpirationTimer = (
  session: ISessionKey,
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): void => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
  }

  // @ts-ignore
  expirationTimer = setTimeout(() => {
    dispatch(condosActions.setIsSessionExpired(true));
  }, getTimeout(session?.expireDate));
};

export const getCondos = (searchType: SearchTypeEnum, isReuse = false): AppThunk => {
  return async (dispatch, getState) => {
    try {
      const {
        condosStore,
        condosLocationPickerStore,
        condoStrictDatesStore,
        condoFlexibleDatePickerStore,
        condoFiltersStore,
      } = getState();
      const { cachedSelectedLocation } = condosStore;
      const requestType = getCondoRequestType(condoStrictDatesStore, condoFlexibleDatePickerStore);

      if (!isReuse && condosStore.pageNumber === DEFAULT_PAGE_NUMBER) {
        dispatch(condosActions.setIsAnytimeSearchWarningVisible(
          shouldDisplayAnytimeWarning(
            requestType,
            condoFlexibleDatePickerStore)));
      }

      if (
        requestType === CondoRequestTypeEnum.Anytime &&
        condoFiltersStore.sortType !== SortTypes.MostPopular &&
        condoFiltersStore.sortType !== SortTypes.Nearest
      ) {
        dispatch(condoFiltersActions.setSortType(SortTypes.MostPopular));
      }

      dispatch(condosActions.setIsFinishedWithError(false));
      dispatch(condosActions.setSelectedCondo(null));

      if (!condosStore.condos) {
        dispatch(condosActions.setCondos([]));
      }

      if (searchType === SearchTypeEnum.Pagination) {
        dispatch(condosActions.setLoadingMore(true));
      } else {
        dispatch(condosActions.setLoading(true));
      }

      if (cancelRequest) {
        cancelRequest();
      }

      const { data }: { data: ICondoSearchResponse } = await axiosInstance.post(
        getCondoRequestUrl(
          searchType,
          condoStrictDatesStore,
          condoFlexibleDatePickerStore,
          isReuse,
        ),
        getCondoSearchBody(searchType, getState, isReuse),
        {
          ...getHeaders(),
          cancelToken: new axios.CancelToken((canceler: Canceler) => {
            cancelRequest = canceler;
          }),
        },
      );

      dispatch(
        condosActions.setCachedSelectedLocation(
          condosLocationPickerStore.selectedLocation
            ? condosLocationPickerStore.selectedLocation
            : cachedSelectedLocation,
        ),
      );
      dispatch(
        condosActions.setCachedDates({
          startDate: condoStrictDatesStore.startDate,
          endDate: condoStrictDatesStore.endDate,
        }),
      );
      dispatch(condosActions.setLoading(false));

      if (data.sessionKey) {
        dispatch(condosActions.setSessionKey(data.sessionKey));
      }

      dispatch(condosActions.setLoadingMore(false));
      dispatch(condosActions.setPrevCondos(data.searchCondos));

      if (searchType === SearchTypeEnum.Pagination) {
        dispatch(condosActions.addCondos(data.searchCondos));
      } else {
        dispatch(condosActions.setCondos(data.searchCondos));
        dispatch(condosActions.setMapCondos(data.searchCondos.slice(zero, DEFAULT_MAP_LIMIT)));
      }

      if (data.sessionKey) {
        dispatch(condosActions.setIsSessionExpired(false));
        // @ts-ignore
        setSessionExpirationTimer(data.sessionKey, dispatch);
      }

      if (data.counters) {
        dispatch(condosActions.setCounters(data.counters));
      }

      if (data.bounds) {
        dispatch(condosActions.setBounds(data.bounds));
      }

      dispatch(condosActions.setLastRequestType(requestType));
      dispatch(condosActions.setIsFinishedWithError(false));
      dispatch(trustYouReviewActions.setReviewModule(null));
      dispatch(condoDetailsActions.setCondoDetails(null));
    } catch (error) {
      console.error(error);

      if (!(error instanceof axios.Cancel)) {
        dispatch(condosActions.setIsFinishedWithError(true));
        dispatch(condosActions.setError(error.toString()));
        dispatch(condosActions.setLoading(false));
        dispatch(condosActions.setLoadingMore(false));
      }
    }
  };
};

export const getCondoRecentSearches = (): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(condosActions.setLoadingRecentSearches(true));

      if (cancelRequest) {
        cancelRequest();
      }
      const { data } = await axiosInstance.get(Urls.CondosRecentSearches, {
        ...getHeaders(),
        cancelToken: new axios.CancelToken((canceler: Canceler) => {
          cancelRequest = canceler;
        }),
      });
      dispatch(condosActions.setRecentSearches(data.recentSearches || []));
      dispatch(condosActions.setLoadingRecentSearches(false));
    } catch (error) {
      console.error(error);
      dispatch(condosActions.setErrorRecentSearches(error.toString()));
      dispatch(condosActions.setLoadingRecentSearches(false));
    }
  };
};
